/**
 * The Flights page's reads (docs/specs/cloud/flights.md): one project's flights, daily entries, plan and line status,
 * Geosoft QC runs and line verdicts, the QC lines' acknowledgements (decision 751's Actioned column), the staff
 * register's names and the aircraft register's registrations, and the names of the people who reviewed lines. Read
 * only; RLS lets every member of the tenant read these tables and no client user read flights, daily entries or QC
 * (20261018000100_m1_policies.sql, 20261019000000_geosoft_qc.sql, 20261025001000_qc_acceptance.sql).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { DailyEntry, Flight, QcAcceptance, QcLineResult, QcRunSummary } from '@teslyn/domain';
import { all, loadProjectPlan, plainPortalError, type ProjectPlan, type Res } from '../portal/api.ts';

export type FlightQcLine = Pick<
  QcLineResult,
  'id' | 'run_id' | 'line_status_id' | 'line_name' | 'line_number' | 'verdict' | 'created_at' | 'deleted_at'
>;

export interface FlightsData extends ProjectPlan {
  readonly flights: readonly Flight[];
  readonly entries: readonly DailyEntry[];
  readonly qcRuns: readonly QcRunSummary[];
  readonly qcLines: readonly FlightQcLine[];
  /** Acknowledge & accepted and its undos (decision 751: a line acknowledged is actioned). */
  readonly qcAcceptances: readonly QcAcceptance[];
  readonly people: readonly { readonly id: string; readonly name: string }[];
  readonly aircraft: readonly { readonly id: string; readonly registration: string }[];
  /**
   * Who reviewed lines, by user id: the staff register's name when the sign-in is linked there (as the refly ticks
   * name people), else the name on the sign-in, else its email.
   */
  readonly reviewers: ReadonlyMap<string, string>;
}

const QC_RUN_COLUMNS =
  'id, project_id, flight_id, kind, family, label, source_path, source_bytes, source_modified_at, geosoft_version, driver_version, status, error, started_at, finished_at, seconds, line_count, fail_count, warn_count, pass_count, notes, created_at, updated_at, updated_by, deleted_at';

export async function loadFlightsData(
  sb: SupabaseClient,
  project: { readonly id: string; readonly tenantId: string },
): Promise<FlightsData> {
  const [plan, flights, entries, qcRuns, qcLines, qcAcceptances, people, aircraft] = await Promise.all([
    loadProjectPlan(sb, project.id),
    all<Flight>((from, to) =>
      sb
        .from('flight')
        .select('*')
        .eq('project_id', project.id)
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    ),
    all<DailyEntry>((from, to) =>
      sb
        .from('daily_entry')
        .select('*')
        .eq('project_id', project.id)
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    ),
    // the spec each run was made with is large and not shown here
    all<QcRunSummary>((from, to) =>
      sb
        .from('qc_run')
        .select(QC_RUN_COLUMNS)
        .eq('project_id', project.id)
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    ),
    // the verdicts only: each line's tests stay in Teslyn Field's Geosoft QC
    all<FlightQcLine>((from, to) =>
      sb
        .from('qc_line_result')
        .select('id, run_id, line_status_id, line_name, line_number, verdict, created_at, deleted_at')
        .eq('project_id', project.id)
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    ),
    all<QcAcceptance>((from, to) =>
      sb
        .from('qc_acceptance')
        .select('*')
        .eq('project_id', project.id)
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    ),
    all<{ id: string; name: string; user_id: string | null }>((from, to) =>
      sb
        .from('person')
        .select('id, name, user_id')
        .eq('tenant_id', project.tenantId)
        .order('id')
        .range(from, to),
    ),
    all<{ id: string; registration: string }>((from, to) =>
      sb
        .from('aircraft')
        .select('id, registration')
        .eq('tenant_id', project.tenantId)
        .order('id')
        .range(from, to),
    ),
  ]);
  const reviewerIds = [
    ...new Set(plan.statuses.map((s) => s.reviewed_by).filter((id): id is string => Boolean(id))),
  ];
  const reviewers = new Map<string, string>();
  if (reviewerIds.length > 0) {
    const r = (await sb.from('app_user').select('id, email, display_name').in('id', reviewerIds)) as Res<
      { id: string; email: string | null; display_name: string | null }[]
    >;
    if (r.error) throw new Error(plainPortalError(r.error));
    const registered = new Map(people.filter((p) => p.user_id).map((p) => [p.user_id!, p.name]));
    for (const u of r.data ?? [])
      reviewers.set(u.id, registered.get(u.id) || u.display_name?.trim() || u.email || 'Someone');
  }
  return { ...plan, flights, entries, qcRuns, qcLines, qcAcceptances, people, aircraft, reviewers };
}
